import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { AlertTriangle, ArrowLeft, Loader2 } from "lucide-react";
import { useCategories } from "~/api/swr/useCategories";
import { useCategoryDetails } from "~/api/swr/useCategoryDetails";
import { useDocuments } from "~/api/v2/useDocuments";
import { deleteCategory, updateDocument } from "~/api/v2/requests";
import { Alert, AlertDescription, AlertTitle } from "~/atoms/alert";
import { Button } from "~/atoms/button";
import { DialogFooter } from "~/atoms/dialog";
import { setMapItems } from "~/helpers/map";
import { DocumentReassignmentTable } from "./DocumentReassignmentTable";

interface Props {
  categoryId: number;
  onBack: () => void;
  onDeleted: () => void;
}

export const DeleteCategoryStep = ({
  categoryId,
  onBack,
  onDeleted,
}: Props) => {
  const { t } = useTranslation();
  const { data: category } = useCategoryDetails(categoryId);
  const { data: allDocuments, mutate: mutateDocuments } = useDocuments({
    includeArchived: true,
  });
  const { data: categories, mutate: mutateCategories } = useCategories();
  const [targetCategoryByDocumentId, setTargetCategoryByDocumentId] = useState<
    Map<number, string>
  >(() => new Map());
  const [isDeleting, setIsDeleting] = useState(false);

  if (!category || !allDocuments || !categories) {
    return (
      <div className="flex justify-center py-8">
        <Loader2 className="text-muted-foreground animate-spin" />
      </div>
    );
  }

  const documents = allDocuments
    .filter((document) => document.category.id === categoryId)
    .sort((a, b) => a.name.localeCompare(b.name, undefined, { numeric: true }));

  const categoryOptions = categories
    .filter((otherCategory) => otherCategory.id !== categoryId)
    .map((otherCategory) => ({
      label: otherCategory.title,
      value: otherCategory.id.toString(),
    }));

  const moves = documents.flatMap((document) => {
    const target = targetCategoryByDocumentId.get(document.id);
    return target === undefined
      ? []
      : [{ documentId: document.id, targetCategoryId: Number(target) }];
  });
  const remainingCount = documents.length - moves.length;

  const setTargetCategories = (
    documentIds: Set<number>,
    targetCategoryId: string,
  ) => {
    setTargetCategoryByDocumentId((prev) =>
      setMapItems(prev, documentIds, targetCategoryId),
    );
  };

  const moveDocumentsAndDelete = async () => {
    setIsDeleting(true);
    try {
      await Promise.all(
        moves.map((move) =>
          updateDocument(move.documentId, {
            categoryId: move.targetCategoryId,
          }),
        ),
      );
      await deleteCategory(categoryId);
      void mutateCategories();
      toast.success(
        t(($) => $.toasts.success.documentsMovedAndCategoryDeleted),
      );
      onDeleted();
    } catch {
      toast.error(t(($) => $.toasts.error.categoryNotRemoved));
    } finally {
      void mutateDocuments();
      setIsDeleting(false);
    }
  };

  return (
    <div className="flex flex-col gap-4">
      <Alert variant="warn">
        <AlertTriangle />
        <AlertTitle>
          {t(($) => $.modal.categoryTierModal.deleteCategoryTitle, {
            title: category.title,
          })}
        </AlertTitle>
        <AlertDescription>
          {t(($) => $.modal.categoryTierModal.deleteCategoryDescription)}
        </AlertDescription>
      </Alert>
      <div className="flex flex-col gap-1">
        <div className="flex items-center justify-between gap-3">
          <h3 className="text-sm font-medium">
            {t(($) => $.modal.categoryTierModal.documentsToMove)}
          </h3>
          <span className="text-muted-foreground text-xs">
            {t(($) => $.modal.categoryTierModal.documentsRemaining, {
              count: remainingCount,
            })}
          </span>
        </div>
        <DocumentReassignmentTable
          documents={documents}
          categoryOptions={categoryOptions}
          targetCategoryByDocumentId={targetCategoryByDocumentId}
          disabled={isDeleting}
          onTargetCategoryChange={setTargetCategories}
        />
      </div>
      <DialogFooter className="sm:justify-between">
        <Button variant="outline" disabled={isDeleting} onClick={onBack}>
          <ArrowLeft />
          {t(($) => $.commons.buttons.back)}
        </Button>
        <Button
          variant="destructive"
          disabled={isDeleting || remainingCount > 0}
          onClick={() => void moveDocumentsAndDelete()}
        >
          {isDeleting && <Loader2 className="animate-spin" />}
          {t(($) => $.modal.categoryTierModal.moveDocumentsAndDelete, {
            title: category.title,
          })}
        </Button>
      </DialogFooter>
    </div>
  );
};
